import React from "react";

import { IconQuestionCircleOutlined } from "~/components/icon";
import type { PolymorphicBoxProps } from "~/components/polymorphic-box";
import { cx } from "~/tools/class-variance";
import { theme } from "~/tools/twin-migration";

import { ComponentLink } from "../../react/component-link/ComponentLink";
import { Tooltip } from "../../react/tooltip/Tooltip";
import { LabelBullet } from "../_label-bullet";

import type { LegendEntry } from "./_legend-entry";
import styles from "./_legend-entry-label.module.css";

const DEFAULT_ELEMENT = "div";

export type LegendEntryLabelProps<TElement extends React.ElementType = typeof DEFAULT_ELEMENT> = PolymorphicBoxProps<
	TElement,
	Omit<LegendEntry, "index" | "active"> & { className?: string }
>;

export const LegendEntryLabel: <TElement extends React.ElementType = typeof DEFAULT_ELEMENT>(
	props: LegendEntryLabelProps<TElement>,
) => React.ReactElement = ({
	color,
	label,
	componentIdentifier,
	children,
	className,
	el: Element = DEFAULT_ELEMENT,
	...props
}: LegendEntryLabelProps<React.ElementType>) => {
	const isUnobserved = label.includes("<unobserved>");
	const isUnsupported = label.includes("<unsupported>");

	return (
		<Element className={cx(styles.legendEntryLabel, className)} {...props}>
			<LabelBullet style={{ background: color }} />

			{children}

			{(isUnobserved || isUnsupported) && (
				<Tooltip
					data-testid="data-missing-help-tooltip"
					overlayClassName={cx("max-w-[280px]")}
					overlayInnerStyle={{
						padding: 12,
						borderRadius: 4,
						backgroundColor: theme("colors.gray.900/90"),
					}}
					placement="topLeft"
					title={
						<div className="space-y-4">
							{isUnobserved && (
								<div>
									Part of the metric labels were not observed. This happens when the agent was not running at the moment
									the relevant data was exchanged. Restart your application to get full visibility.
								</div>
							)}

							{isUnsupported && (
								<div>
									Part of the metric labels was not supported. This happens when the agent does not support gathering of
									that data.
								</div>
							)}
						</div>
					}
					trigger="click"
				>
					<IconQuestionCircleOutlined className="h-3 w-3 text-gray-800" data-testid="data-missing-help-icon" />
				</Tooltip>
			)}

			{componentIdentifier != null && (
				<ComponentLink
					className="mr-2 flex-none font-medium text-10"
					componentIdentifier={componentIdentifier}
					text="Open"
				/>
			)}
		</Element>
	);
};
